import G6, { Graph, IGroup, IShape, ModelConfig } from '@antv/g6';
import buscouplerImg from '@/assets/images/circuit/buscoupler.png';
import incomingImg from '@/assets/images/circuit/incoming.png';
import outgoingImg from '@/assets/images/circuit/outgoing.png';
import gridbillingImg from '@/assets/images/circuit/gridbilling.png';
import transformerImg from '@/assets/images/circuit/transformer.png';
import pressureImg from '@/assets/images/circuit/pressure.png';
import knifeImg from '@/assets/images/circuit/knife.png';
import roomPng from '../../imgs/room.png';
import alarmSvg from '../../imgs/alarm.svg';
import rightArrowSvg from '../../imgs/doubleRightArrow.svg';
import leftArrowSvg from '../../imgs/doubleLeftArrow.svg';
import { CircuitType } from '@/api/circuit';
import { GraphNode, levelDisplay, SwitchStatus } from '../../utils';
import { isNil } from 'lodash-es';
import { Themes } from '@maxtropy/components';

const imgMap = {
  [CircuitType.BUS_COUPLER_CIRCUIT]: buscouplerImg,
  [CircuitType.GRID_BILLING_CIRCUIT]: gridbillingImg,
  [CircuitType.INCOMING_CIRCUIT]: incomingImg,
  [CircuitType.OUTGOING_CIRCUIT]: outgoingImg,
  [CircuitType.TRANSFORMER_CIRCUIT]: transformerImg,
  [CircuitType.VOLTAGE_CONVERSION_CIRCUIT]: pressureImg,
  [CircuitType.LEAD_WIRE_SWITCH_CIRCUIT]: knifeImg,
};

export const nodeSize = [200, 104];
export const viewNodeSize = [200, 40];
export const nodeGap = [100, 114];

export function registerG6(isOverView?: boolean, toNextNode?: (circuitId?: number) => void, theme?: Themes) {
  const isDark = theme === Themes.DARK;
  const nodeFill = isDark ? '#2D2D2D' : '#FFFFFF';
  const activeNodeFill = isDark ? '#1E3557' : '#EAF4FF';
  const borderColor = isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)';
  const textColor = isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)';
  const secondaryTextColor = isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.65)';
  const subtleFill = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)';
  const hoverColor = isDark ? '#FFFFFF' : '#4A90E2';
  // 自定义连接线
  G6.registerEdge('customLine', {
    draw(cfg, group) {
      const startPoint = cfg?.startPoint!;
      const endPoint = cfg?.endPoint!;
      const shape = group.addShape('path', {
        attrs: {
          stroke: '#4A90E2',
          path: [
            ['M', startPoint.x, startPoint.y],
            ['L', (startPoint.x + endPoint.x) / 2, startPoint.y],
            ['L', (startPoint.x + endPoint.x) / 2, endPoint.y],
            ['L', endPoint.x, endPoint.y],
          ],
        },
        name: 'path-shape',
      });
      return shape;
    },
    setState(name, value, item) {
      const shape = item!.get('group').find((ele: any) => ele.get('name') === 'path-shape');
      if (name === 'highlight') {
        shape.attr({
          stroke: value ? '#16DD8E' : '#4A90E2',
          lineWidth: value ? 2 : 1,
        });
      }
    },
  });

  if (isOverView) {
    // 自定义节点-不带数据
    G6.registerNode(
      'customNode',
      {
        draw: function drawShape(cfg, group) {
          const r = 2;
          const w = viewNodeSize[0];
          let h = viewNodeSize[1];
          const { name, type } = cfg.value as GraphNode;

          // 名字换行
          // const { text, lineCount } = wrapText(name as string, 120);
          // const extendH = lineCount - 2 > 0 ? (lineCount - 2) * 14 : 0;
          // h += extendH;

          // 主体框
          const shape = group!.addShape('rect', {
            attrs: {
              x: -w / 2,
              y: -h / 2,
              width: w,
              height: h,
              stroke: borderColor,
              radius: r,
              fill: nodeFill,
              cursor: 'pointer',
            },
            name: 'main-box',
          });

          // 图标
          group!.addShape('image', {
            attrs: {
              x: -w / 2 + 10,
              y: -h / 2 + 10,
              width: 20,
              height: 20,
              cursor: 'pointer',
              img: imgMap[type as CircuitType],
            },
            name: 'main-box-icon',
          });

          // 两侧装饰边
          group!.addShape('rect', {
            attrs: {
              x: -w / 2,
              y: -h / 2,
              width: 2,
              height: 40,
              radius: [2, 0, 0, 2],
              fill: '#4A90E2',
            },
            name: 'main-box-left-line',
          });
          group!.addShape('rect', {
            attrs: {
              x: w / 2 - 2,
              y: -h / 2,
              width: 2,
              height: 40,
              radius: [0, 2, 2, 0],
              fill: '#4A90E2',
            },
            name: 'main-box-right-line',
          });

          const truncatedName = truncateText(name, 140);

          // 名字
          group!.addShape('text', {
            attrs: {
              x: -w / 2 + 38,
              y: -h / 2 + 26,
              textBaseline: 'bottom',
              text: truncatedName,
              fill: textColor,
              cursor: 'pointer',
            },
            name: 'main-box-circuit-name',
          });

          return shape;
        },

        setState(name, value, item) {
          const data = item?.getModel();
          // 高亮主体边框
          const changeMainBox = () => {
            const shape = item!.get('group').find((ele: any) => ele.get('name') === 'main-box');
            if (data?.isSelected) {
              shape.attr('stroke', '#16DD8E');
              shape.attr('fill', activeNodeFill);
            } else if (data?.isHighlight) {
              shape.attr('stroke', borderColor);
              shape.attr('fill', activeNodeFill);
            } else {
              shape.attr('fill', nodeFill);
              shape.attr('stroke', borderColor);
            }
            if (data?.isHover && !data?.isSelected) {
              shape.attr('stroke', hoverColor);
            }
          };

          if (name === 'highlight') {
            data!.isHighlight = value;
            changeMainBox();
          }
          if (name === 'hover') {
            data!.isHover = value;
            changeMainBox();
          }
          if (name === 'isSelected') {
            data!.isSelected = value;
            changeMainBox();
          }
        },
        getAnchorPoints() {
          return [
            [0, 0.5], // 左边中点，对应 index 0
            [1, 0.5], // 右边中点，对应 index 1
          ];
        },
      },
      'single-node'
    );
  } else {
    // 自定义节点-带数据
    G6.registerNode(
      'customNodeWithData',
      {
        draw: function drawShape(cfg, group) {
          const r = 2;
          const {
            name,
            type,
            isAlarm,
            electricCurrentA,
            electricCurrentB,
            electricCurrentC,
            power,
            target,
            targetInCurrentStation,
            switchStatus,
            hasDevice,
          } = cfg.value as GraphNode;

          const hasAllNoData =
            isNil(electricCurrentA) && isNil(electricCurrentB) && isNil(electricCurrentC) && isNil(power);

          const w = hasDevice && !hasAllNoData ? nodeSize[0] : viewNodeSize[0];
          let h = hasDevice && !hasAllNoData ? nodeSize[1] : viewNodeSize[1];

          // 名字换行
          // const { text, lineCount } = wrapText(name as string, 120);
          // const extendH = lineCount - 2 > 0 ? (lineCount - 2) * 14 : 0;
          // h += extendH;

          // 主体框
          const shape = group!.addShape('rect', {
            attrs: {
              x: -w / 2,
              y: -h / 2,
              width: w,
              height: h,
              stroke: borderColor,
              radius: r,
              fill: nodeFill,
              cursor: 'pointer',
            },
            name: 'main-box',
          });

          // 图标
          group!.addShape('image', {
            attrs: {
              x: -w / 2 + 10,
              y: -h / 2 + 10,
              width: 20,
              height: 20,
              cursor: 'pointer',
              img: imgMap[type as CircuitType],
            },
            name: 'main-box-icon',
          });

          // 两侧装饰边
          group!.addShape('rect', {
            attrs: {
              x: -w / 2,
              y: -h / 2,
              width: 2,
              height: 40,
              radius: [2, 0, 0, 2],
              fill: '#4A90E2',
            },
            name: 'main-box-left-line',
          });
          group!.addShape('rect', {
            attrs: {
              x: w / 2 - 2,
              y: -h / 2,
              width: 2,
              height: 40,
              radius: [0, 2, 2, 0],
              fill: '#4A90E2',
            },
            name: 'main-box-right-line',
          });

          const truncatedName = truncateText(name, 140);
          // 名字
          const circuitName = group!.addShape('text', {
            attrs: {
              x: -w / 2 + 38,
              y: -h / 2 + 26,
              textBaseline: 'bottom',
              text: truncatedName,
              fill: textColor,
              cursor: 'pointer',
            },
            name: 'main-box-circuit-name',
          });

          // 存在报警
          if (isAlarm) {
            const alarmCenterX = -w / 2 + 181;
            const alarmCenterY = -h / 2 + 20;

            circuitName.attr({
              fill: '#E64242',
            });

            const createRipple = (delay = 0, baseR = 4, rippleRange = 6, color = '#FF4D4F') => {
              const circle = group!.addShape('circle', {
                attrs: {
                  x: alarmCenterX,
                  y: alarmCenterY,
                  r: baseR,
                  fill: color,
                  opacity: 0.4,
                },
                name: 'alarm-halo',
                draggable: false,
              });

              circle.animate(
                (ratio: any) => ({
                  r: baseR + ratio * rippleRange,
                  opacity: 0.6 * (1 - ratio),
                }),
                {
                  duration: 1200,
                  repeat: true,
                  delay,
                  easing: 'easeCubic',
                }
              );
            };

            createRipple(0, 2, 4); // 第一圈光圈
            createRipple(600, 6, 6); // 第二圈光圈

            const alarmIcon = group!.addShape('image', {
              attrs: {
                x: -w / 2 + 174,
                y: -h / 2 + 12,
                width: 14,
                height: 14,
                img: alarmSvg,
                cursor: 'pointer',
              },
              name: 'alarm-icon',
            });
            alarmIcon.toFront();

            alarmIcon.on('click', () => {
              window.open(`/dmes/alarm/record?circuitId=${cfg.id}`, '_blank');
            });

            addHoverTooltipShape({
              group,
              targetShape: alarmIcon,
              tooltipText: '查看报警详情',
              tooltipKey: 'inter-btn',
            });
          }

          if (hasDevice && !hasAllNoData) {
            // 分割线
            group!.addShape('rect', {
              attrs: {
                x: -w / 2 + 8,
                y: -h / 2 + 39,
                width: 184,
                height: 1,
                cursor: 'pointer',
                fill: borderColor,
              },
              name: 'main-box-split-line',
            });

            // 电流背景
            group!.addShape('rect', {
              attrs: {
                x: -w / 2 + 8,
                y: -h / 2 + 44,
                width: 184,
                height: 24,
                radius: 2,
                cursor: 'pointer',
                fill: subtleFill,
              },
              name: 'main-box-power-bg',
            });

            // A/B/C 相电流
            const currentFontSize = 12;
            const totalWidth = 184;
            const segmentWidth = totalWidth / 3;
            const baseX = -w / 2 + 8; // 起始 X 坐标

            const currentAText = isNil(electricCurrentA) ? '--' : `${electricCurrentA}A`;
            const currentBText = isNil(electricCurrentB) ? '--' : `${electricCurrentB}A`;
            const currentCText = isNil(electricCurrentC) ? '--' : `${electricCurrentC}A`;

            // A相：靠左，textAlign = 'left'，稍微往右缩进 4px
            const currentAShape = group!.addShape('text', {
              attrs: {
                x: baseX + 8,
                y: -h / 2 + 62,
                textBaseline: 'bottom',
                textAlign: 'left',
                text: truncateText(currentAText, segmentWidth - 8, currentFontSize),
                fontSize: currentFontSize,
                fill: '#FFCB47',
                cursor: 'pointer',
              },
              name: 'main-box-current-A',
            });
            addHoverTooltipShape({
              group,
              targetShape: currentAShape,
              tooltipText: currentAText,
              tooltipKey: 'inter-btn',
            });

            // B相：居中
            const currentBShape = group!.addShape('text', {
              attrs: {
                x: baseX + segmentWidth * 1.5,
                y: -h / 2 + 62,
                textBaseline: 'bottom',
                textAlign: 'center',
                text: truncateText(currentBText, segmentWidth - 8, currentFontSize),
                fontSize: currentFontSize,
                fill: '#59D744',
                cursor: 'pointer',
              },
              name: 'main-box-current-B',
            });

            addHoverTooltipShape({
              group,
              targetShape: currentBShape,
              tooltipText: currentBText,
              tooltipKey: 'inter-btn',
            });

            // C相：靠右，textAlign = 'right'，稍微往左缩进 4px
            const currentCShape = group!.addShape('text', {
              attrs: {
                x: baseX + segmentWidth * 3 - 8,
                y: -h / 2 + 62,
                textBaseline: 'bottom',
                textAlign: 'right',
                text: truncateText(currentCText, segmentWidth - 8, currentFontSize),
                fontSize: currentFontSize,
                fill: '#FF4D4F',
                cursor: 'pointer',
              },
              name: 'main-box-current-C',
            });

            addHoverTooltipShape({
              group,
              targetShape: currentCShape,
              tooltipText: currentCText,
              tooltipKey: 'inter-btn',
            });

            // 功率背景
            group!.addShape('rect', {
              attrs: {
                x: -w / 2 + 8,
                y: -h / 2 + 72,
                width: 184,
                height: 24,
                radius: 2,
                fill: subtleFill,
                cursor: 'pointer',
              },
            });

            group!.addShape('text', {
              attrs: {
                x: -w / 2 + 14,
                y: -h / 2 + 90,
                textBaseline: 'bottom',
                text: '功率(kW)',
                fontSize: 12,
                fill: secondaryTextColor,
                cursor: 'pointer',
              },
              name: 'main-box-power-label',
            });

            const powerValue = isNil(power) ? '--' : `${power}kW`;
            const powerShape = group!.addShape('text', {
              attrs: {
                x: -w / 2 + 184,
                y: -h / 2 + 90,
                textBaseline: 'bottom',
                text: powerValue,
                fontSize: 12,
                fill: textColor,
                cursor: 'pointer',
                textAlign: 'right',
              },
              name: 'main-box-power-value',
            });

            addHoverTooltipShape({
              group,
              targetShape: powerShape,
              tooltipText: powerValue,
              tooltipKey: 'inter-btn',
            });
          }

          // 展开/收起按钮
          if ((cfg?.children as any)?.length) {
            group.addShape('circle', {
              attrs: {
                fill: '#3570BD',
                x: w / 2 + 20,
                y: 0,
                r: 10,
                stroke: '#4A90E2',
                lineWidth: 1,
                cursor: 'pointer',
              },
              name: 'collapse-circle',
            });

            group.addShape('text', {
              attrs: {
                x: w / 2 + 20,
                y: 0,
                zIndex: 10,
                fill: '#fff',
                text: cfg.collapsed ? getAllChildrenCount(cfg.children as any) : '—',
                textBaseline: 'middle',
                textAlign: 'center',
                cursor: 'pointer',
              },
              name: 'collapse-text',
            });

            group.addShape('rect', {
              attrs: {
                x: w / 2,
                y: -0.5,
                width: 10,
                height: 1,
                fill: '#4A90E2',
              },
              name: 'collapse-text',
            });
          }

          // 母联回路右侧
          // 需要判断rightTarget是否在当前站房
          // 需要判断是否为开闸
          if (
            type === CircuitType.BUS_COUPLER_CIRCUIT &&
            target &&
            !targetInCurrentStation &&
            switchStatus !== SwitchStatus.OPENING
          ) {
            const nodeValue = cfg.value as GraphNode;

            group.addShape('path', {
              attrs: {
                stroke: '#4A90E2',
                lineWidth: 1,
                path: [
                  ['M', w / 2, 0],
                  ['L', w / 2 + 10, 0],
                ],
              },
              name: 'bus-coupler-link-line',
            });
            const circle = group.addShape('circle', {
              attrs: {
                fill: '#3570BD',
                x: w / 2 + 20,
                y: 0,
                r: 10,
                stroke: '#4A90E2',
                lineWidth: 1,
                cursor: 'pointer',
              },
              name: 'bus-coupler-circle',
            });
            const icon = group!.addShape('image', {
              attrs: {
                x: w / 2 + 15,
                y: -5,
                width: 10,
                height: 10,
                img: nodeValue?.target?.fromOrTo === 1 ? rightArrowSvg : leftArrowSvg,
                cursor: 'pointer',
              },
              name: 'bus-coupler-img',
            });

            icon.on('click', () => {
              toNextNode?.(nodeValue.target?.circuitId);
            });

            const direction = nodeValue?.target?.fromOrTo === 1 ? '引至' : '引自';

            addHoverTooltipShape({
              group,
              targetShape: icon,
              tooltipText: `${direction}${nodeValue?.target?.roomName ?? ''} ${
                nodeValue?.target?.circuitName ?? ''
              }回路`,
              tooltipKey: 'inter-btn',
            });
          }

          return shape;
        },

        getAnchorPoints() {
          return [
            [0, 0.5],
            [1, 0.5],
          ];
        },

        setState(name, value, item) {
          const data = item?.getModel();

          const changeMainBox = () => {
            const shape = item!.get('group').find((ele: any) => ele.get('name').startsWith('main-box'));
            if (data?.isSelected) {
              shape.attr('stroke', '#16DD8E');
              shape.attr('fill', activeNodeFill);
            } else if (data?.isHighlight) {
              shape.attr('stroke', borderColor);
              shape.attr('fill', activeNodeFill);
            } else {
              shape.attr('fill', nodeFill);
              shape.attr('stroke', borderColor);
            }
            if (data?.isHover && !data?.isSelected) {
              shape.attr('stroke', hoverColor);
            }
          };
          if (name === 'collapsed') {
            const count = getAllChildrenCount(item?.getModel().children as any);
            const textShape = item!.get('group').find((ele: any) => ele.get('name') === 'collapse-text');
            textShape.attr('text', value ? count : '—');
          }
          if (name === 'highlight') {
            data!.isHighlight = value;
            changeMainBox();
          }
          if (name === 'collapse-hover') {
            const collapseCircle = item!.get('group').find((ele: any) => ele.get('name') === 'collapse-circle');
            collapseCircle && collapseCircle.attr('stroke', value ? hoverColor : '#4A90E2');
          }
          if (name === 'bus-coupler-hover') {
            const collapseCircle = item!.get('group').find((ele: any) => ele.get('name') === 'bus-coupler-circle');
            collapseCircle && collapseCircle.attr('stroke', value ? hoverColor : '#4A90E2');
          }
          if (name === 'hover') {
            data!.isHover = value;
            changeMainBox();
          }
          if (name === 'isSelected') {
            data!.isSelected = value;
            changeMainBox();
          }
        },
      },
      'single-node'
    );
  }

  // 自定义combo
  G6.registerCombo(
    'customComb',
    {
      drawShape(cfg, group) {
        const self = this;
        cfg!.padding = cfg?.padding || [50, 50, 20, 20];
        const style = self.getShapeStyle(cfg);

        const width = style.width;
        const height = style.height;

        const rect = group!.addShape('rect', {
          attrs: {
            ...style,
            x: -width / 2,
            y: -height / 2,
            fill: 'transparent',
            stroke: borderColor,
            width: width,
            height: height,
            lineDash: [4, 4],
            lineWidth: 1,
            radius: 4,
          },
          name: 'combo-keyShape',
        });

        return rect;
      },

      drawLabel(cfg, group) {
        const keyShape = group.get('children')?.find((item: any) => item.get('name') === 'combo-keyShape');
        const bbox = keyShape?.getBBox();

        const level = levelDisplay[(cfg.level ?? 1) as number];

        const icon = group!.addShape('image', {
          attrs: {
            x: bbox.minX,
            y: bbox.minY,
            width: 20,
            height: 20,
            img: roomPng,
          },
          name: 'combo-icon',
        });

        const label = group.addShape('text', {
          attrs: {
            text: cfg.label,
            fill: textColor,
            fontSize: 14,
            textAlign: 'left',
            textBaseline: 'middle',
            x: bbox.minX + 30,
            y: bbox.minY - 20,
          },
          name: 'combo-label',
        });

        const labelBBox = label.getBBox();

        // 测量等级文本宽度
        const textWidth = measureText(level, 12);

        // 等级标签背景
        const levelBg = group.addShape('rect', {
          attrs: {
            x: labelBBox.maxX + 10,
            y: bbox.minY - 30,
            width: textWidth + 16,
            height: 22, // 固定高度
            stroke: borderColor,
            fill: subtleFill, // 根据等级设置背景色
            radius: 2,
          },
          name: 'combo-level-bg',
        });

        // 等级文本
        const levelTextShape = group.addShape('text', {
          attrs: {
            text: level,
            fill: textColor,
            fontSize: 12,
            textAlign: 'center',
            textBaseline: 'middle',
            x: labelBBox.maxX + 10 + (textWidth + 16) / 2,
            y: bbox.minY - 20,
          },
          name: 'combo-level-text',
        });

        return label;
      },

      // 添加更新逻辑以支持动态变化
      update(cfg, combo) {
        const group = combo.get('group');
        const keyShape = group.find((item: any) => item.get('name') === 'combo-keyShape');
        const label = group.find((item: any) => item.get('name') === 'combo-label');
        const icon = group.find((item: any) => item.get('name') === 'combo-icon');
        const levelBg = group.find((item: any) => item.get('name') === 'combo-level-bg');
        const levelText = group.find((item: any) => item.get('name') === 'combo-level-text');

        if (keyShape) {
          keyShape.attr({
            width: cfg.style?.width! + 70,
            height: cfg.style?.height! + 40,
            x: -cfg.style?.width! / 2 - 20,
            y: -cfg.style?.height! / 2 - 20,
          });

          const bbox = keyShape.getBBox();

          if (icon) {
            icon.attr({
              x: bbox.minX,
              y: bbox.minY - 30,
            });
          }
          if (label) {
            label.attr({
              x: bbox.minX + 30,
              y: bbox.minY - 20,
            });

            // 更新等级标签位置（需要先更新标签文本）
            const labelBBox = label.getBBox();

            if (levelBg && levelText) {
              const level = levelDisplay[(cfg.level ?? 1) as number];
              const textWidth = measureText(level, 12);
              const levelPadding = [8, 5];

              levelBg.attr({
                x: labelBBox.maxX + 10,
                y: bbox.minY - 30,
                width: textWidth + levelPadding[0] * 2,
              });

              levelText.attr({
                x: labelBBox.maxX + 10 + (textWidth + levelPadding[0] * 2) / 2,
                y: bbox.minY - 19,
              });
            }
          }
        }
      },
    },
    'rect'
  );
}

function getAllChildrenCount(list: any[]): number {
  let count = 0;
  const traverse = (list: any[]) => {
    count += list.length;
    list.forEach(item => {
      item.children && traverse(item.children);
    });
  };
  traverse(list);
  return count;
}

// 文字指定宽度换行
function wrapText(text?: string, maxWidth = 120) {
  if (!text) return { text: '--', lineCount: 1 };
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  const words = text!.split('');
  const lines = [];
  let currentLine = words[0];
  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = context!.measureText(currentLine + word).width;
    if (width > maxWidth) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine += word;
    }
  }
  lines.push(currentLine);
  return { text: lines.join('\n'), lineCount: lines.length };
}

// 1. 添加文本宽度计算函数
function getTextWidth(text: string, fontSize: number = 14): number {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  context!.font = `${fontSize}px Arial`;
  return context!.measureText(text).width;
}

// 2. 添加文本截断函数
function truncateText(text: string, maxWidth: number, fontSize: number = 14): string {
  const ellipsis = '...';
  if (getTextWidth(text, fontSize) <= maxWidth) {
    return text;
  }

  let truncated = text;
  while (getTextWidth(truncated + ellipsis, fontSize) > maxWidth && truncated.length > 0) {
    truncated = truncated.slice(0, -1);
  }

  return truncated + ellipsis;
}

function measureText(text: string, fontSize: number = 12) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (context) {
    context.font = `${fontSize}px sans-serif`;
    return context.measureText(text).width;
  }
  return text.length * fontSize * 0.6; // 估算值
}

export function highlightRecursive(graph: Graph, nodeId: string) {
  const visitedUpNodes = new Set<string>();
  const visitedDownNodes = new Set<string>();
  const visitedEdges = new Set<string>();

  function dfsUp(currentId: string) {
    if (visitedUpNodes.has(currentId)) return;
    visitedUpNodes.add(currentId);

    const inEdges = graph.getEdges().filter(e => e.getTarget().getID() === currentId);
    inEdges.forEach(edge => {
      const sourceId = edge.getSource().getID();
      visitedEdges.add(edge.getID());
      dfsUp(sourceId);
    });
  }

  function dfsDown(currentId: string) {
    if (visitedDownNodes.has(currentId)) return;
    visitedDownNodes.add(currentId);

    const outEdges = graph.getEdges().filter(e => e.getSource().getID() === currentId);
    outEdges.forEach(edge => {
      const targetId = edge.getTarget().getID();
      visitedEdges.add(edge.getID());
      dfsDown(targetId);
    });
  }

  // 清除旧状态
  graph.getNodes().forEach(node => graph.clearItemStates(node));
  graph.getEdges().forEach(edge => graph.clearItemStates(edge));

  // 执行上下游递归
  dfsUp(nodeId);
  dfsDown(nodeId);

  const allNodeIds = new Set([
    ...visitedUpNodes,
    ...visitedDownNodes,
    nodeId, // 当前点击的节点
  ]);

  allNodeIds.forEach(id => {
    const item = graph.findById(id);
    item && graph.setItemState(item, 'highlight', true);
  });

  visitedEdges.forEach(id => {
    const edge = graph.findById(id);
    edge && graph.setItemState(edge, 'highlight', true);
    edge.toFront();
  });
}

type TooltipConfig = {
  group: IGroup;
  targetShape: IShape;
  tooltipText: string;
  tooltipKey: string;
  onClick?: () => void;
  padding?: number;
  offsetY?: number;
};

function addHoverTooltipShape({
  group,
  targetShape,
  tooltipText,
  tooltipKey,
  onClick,
  padding = 8,
  offsetY = 6,
}: TooltipConfig) {
  const canvas = group.get('canvas');

  const bgName = `tooltip-bg-${tooltipKey}`;
  const textName = `tooltip-text-${tooltipKey}`;
  const triangleName = `tooltip-triangle-${tooltipKey}`;

  const removeTooltip = () => {
    [bgName, textName, triangleName].forEach(name => {
      const shape = group.find(ele => ele.get('name') === name);
      if (shape) group.removeChild(shape);
    });
    canvas.draw();
  };

  targetShape.on('mouseenter', () => {
    const bbox = targetShape.getBBox();
    const textWidth = measureText(tooltipText);
    const tooltipWidth = textWidth + padding * 2;
    const tooltipHeight = 24;
    const triangleHeight = 6;
    const triangleWidth = 10;

    // 以目标 shape 的中心点居中对齐
    const shapeCenterX = bbox.minX + bbox.width / 2;
    const tooltipX = shapeCenterX - tooltipWidth / 2;
    const tooltipY = bbox.minY - tooltipHeight - triangleHeight - offsetY;

    // 背景框
    group.addShape('rect', {
      attrs: {
        x: tooltipX,
        y: tooltipY,
        width: tooltipWidth,
        height: tooltipHeight,
        fill: '#333',
        radius: 4,
        shadowColor: 'rgba(0,0,0,0.3)',
        shadowBlur: 4,
      },
      name: bgName,
    });

    // 文本
    group.addShape('text', {
      attrs: {
        x: tooltipX + tooltipWidth / 2,
        y: tooltipY + tooltipHeight / 2,
        text: tooltipText,
        fill: 'rgba(255,255,255,0.85)',
        fontSize: 12,
        textAlign: 'center',
        textBaseline: 'middle',
      },
      name: textName,
    });

    // 三角箭头
    group.addShape('path', {
      attrs: {
        path: [
          ['M', shapeCenterX - triangleWidth / 2, tooltipY + tooltipHeight],
          ['L', shapeCenterX + triangleWidth / 2, tooltipY + tooltipHeight],
          ['L', shapeCenterX, tooltipY + tooltipHeight + triangleHeight],
          ['Z'],
        ],
        fill: '#333',
      },
      name: triangleName,
    });

    canvas.draw();
  });

  targetShape.on('mouseleave', removeTooltip);

  if (onClick) {
    targetShape.on('click', onClick);
  }
}
